import React from 'react';

import Utilities from '../common/Utilities.jsx';

const Position = {
    Above: 'above',
    Below: 'below',
    Visible: 'visible'
}

function Landmark(props) {
    const containerRef = React.useRef(undefined);
    const [id, setId] = React.useState(undefined);
    React.useEffect(() => setId(Utilities.generateUniqueId()), []);
    if (!id) {
        return null;
    }
    containerRef.current = props.containerRef ?? window.document.body;
    return <_Landmark {...props} containerRef={containerRef} />
}

function _Landmark(props) {
    const landmarkRef = React.useRef(undefined);

    // Scroll listener
    const [scrollInfo, setScrollInfo] = React.useState({ x: 0, y: 0 });
    const scrollEventListenerAdded = React.useRef(false);

    const scrollDependencyList = [];
    if (!props.useWindow) {
        scrollDependencyList.push(props.containerRef);
    }
    const handleScroll = () => {
        let x = props.containerRef.current.scrollLeft;
        if (x == undefined || window.scrollX > x) {
            x = window.scrollX;
        }
        let y = props.containerRef.current.scrollTop;
        if (y == undefined || window.scrollY > y) {
             y = window.scrollY
        }
        setScrollInfo({ x, y });
    };
    React.useEffect(() => {
        const containerElement = props.useWindow ? window : props.containerRef.current;
        if (containerElement && scrollEventListenerAdded.current === false) {
            containerElement.addEventListener('scroll', handleScroll, { passive: true });
        }
        scrollEventListenerAdded.current = true;

        return () => {
            if (scrollEventListenerAdded && containerElement) {
                containerElement.removeEventListener('scroll', handleScroll);
            }
            scrollEventListenerAdded.current = false;
        };
    }, scrollDependencyList);

    const [active, setActive] = React.useState(undefined);
    React.useEffect(() => {
        if (!active && props.active) {
            setActive(true);
            setTimeout(handleScroll, 0);
        }
    }, [props.active]);

    // Resize listener
    const getDimensions = () => {
        if (props.onDimensions) {
            return props.onDimensions();
        }
        const containerElement = props.containerRef.current;
        if (!containerElement) {
            return { height: window.innerHeight, width: window.innerWidth };
        }
        return { height: containerElement.offsetHeight, width: containerElement.offsetWidth };
    }
    const [containerRect, setContainerRect] = React.useState({ bottom: 0, left: 0, right: 0, top: 0 });
    const [dimensions, setDimensions] = React.useState(getDimensions());
    const resizeTimeout = React.useRef(undefined);
    const resizeEventListenerAdded = React.useRef(false);
    const updateDimensions = () => {
        setDimensions(getDimensions())
        if (props.containerRef && props.containerRef.current) {
            const rect = props.containerRef.current.getBoundingClientRect();
            if (props.useWindow) {
                rect.y += window.scrollY;
            }
            setContainerRect(rect);
        }
    }

    React.useEffect(() => {
        updateDimensions();

        function handleResize() {
            if (resizeTimeout.current) {
                clearTimeout(resizeTimeout.current);
            }
            resizeTimeout.current = setTimeout(updateDimensions, props.resizeTimeout || 15);
        }
        const containerElement = props.containerRef.current;
        if (containerElement && resizeEventListenerAdded.current === false) {
            containerElement.addEventListener('resize', handleResize, { passive: true });
        }
        resizeEventListenerAdded.current = true;

        return () => {
            if (resizeEventListenerAdded && containerElement) {
                containerElement.removeEventListener('resize', handleResize);
            }
            resizeEventListenerAdded.current = false;
        };
    }, [props.containerRef]);

    // Position handling
    const previousPositionRef = React.useRef(undefined);
    const [delayed, setDelayed] = React.useState(false);
    const [discovered, setDiscovered] = React.useState(false);
    const [position, setPosition] = React.useState(undefined);

    React.useLayoutEffect(() => {
        if (delayed) {
            return;
        }
        if (landmarkRef.current) {
            const rect = landmarkRef.current.getBoundingClientRect();
            const offsetBottom = (props.offsetBottom || 0) - containerRect.top;
            const offsetLeft = (props.offsetLeft || 0) + containerRect.left;
            const offsetRight = (props.offsetRight || 0) - containerRect.left;
            const offsetTop = (props.offsetTop || 0) + containerRect.top;
            if (rect.top >= offsetTop &&
                rect.bottom < dimensions.height - offsetBottom &&
                rect.left >= offsetLeft &&
                rect.right < dimensions.width - offsetRight) {
                setPosition(Position.Visible);
            } else if (rect.top > dimensions.height - offsetBottom) {
                setPosition(Position.Below);
            } else {
                setPosition(Position.Above);
            }
        }
    }, [landmarkRef, scrollInfo, dimensions, delayed]);

    React.useLayoutEffect(() => {
        if (delayed) {
            return;
        }
        if (previousPositionRef.current === undefined && position !== undefined) {
            previousPositionRef.current = position;

            if (position == Position.Visible) {
                // Visible on load
                if (props.onEnter) {
                    if (!discovered) {
                        setDelayed(true);
                        setPosition(undefined);
                        previousPositionRef.current = undefined;
                        setTimeout(() => setDelayed(false), 0);
                    } else {
                        props.onEnter();
                    }
                }
                setDiscovered(true);
            }
        }
        const _enter = () => {
            setDiscovered(true);

            if (props.onEnter) {
                props.onEnter(previousPositionRef.current);
            }
        }
        const _leave = () => {
            if (props.onLeave) {
                props.onLeave(position);
            }
        }
        if (position === Position.Visible && (previousPositionRef.current === Position.Below || previousPositionRef.current === Position.Above)) {
            // Entered from above or below
            _enter();
            previousPositionRef.current = position;
        }
        if ((position == Position.Above || position == Position.Below) && previousPositionRef.current == Position.Visible) {
            // No longer visible => Update previous position
            previousPositionRef.current = position;
            _leave();
        }
        if ((position == Position.Above && previousPositionRef.current == Position.Below) || (position == Position.Below && previousPositionRef.current == Position.Above)) {
            // Rapid scroll => Fire the events
            _enter();
            _leave();
        }
    }, [position]);

    return <span ref={landmarkRef} style={{ fontSize: 0 }} />
}

function TestViewNav() {
    const [sticky, setSticky] = React.useState(false);
    const handleEnter = (position) => {
        setSticky(false);
    }
    const handleLeave = (position) => {
        if (position == Position.Below) {
            return;
        }
        setSticky(true);
    }
    return (
        <div style={{ height: '200%', position: 'relative' }}>
            <div style={{ height: '25%' }} />
            <div>
                <Landmark onEnter={handleEnter} onLeave={handleLeave} />
                <div style={{ background: 'darkblue', color: 'white', position: sticky ? 'fixed' : 'relative', top: 0, width: '100%' }}>
                    <div style={{ height: '40px', lineHeight: '40px' }}>Landmark 1</div>
                </div>
                <div style={{ background: 'darkblue', color: 'white' }}>
                    <div style={{ display: sticky ? 'block' : 'none', height: '40px', lineHeight: '40px' }}>Landmark 1</div>
                </div>
            </div>
        </div>
    )
}

function TestViewGrid() {
    return (
        <div style={{ display: 'flex', flexWrap: 'wrap', padding: '20px 0 20px 20px' }}>
            {Array.from(Array(40).keys()).map(d => <GridView key={d} index={d} />)}
        </div>
    )
}

function GridView(props) {
    const [animate, setAnimate] = React.useState(false);
    const handleEnter = () => setAnimate(true);
    return (
        <div style={{
            background: 'black',
            color: 'white',
            fontSize: '40px',
            height: '100px',
            lineHeight: '100px',
            opacity: animate ? 1 : 0,
            margin: '20px',
            textAlign: 'center',
            transition: 'opacity 1s ease-in-out',
            width: 'calc(33% - 40px)'
        }}>
            <Landmark onEnter={handleEnter} />
            {props.index}
        </div>
    )
}

export { Landmark as Component, Position, TestViewGrid, TestViewNav };